import { Button, Checkbox, Flex, Tag, Text, Textarea, Tooltip, useToast } from "@chakra-ui/react";
import React, { useState } from "react";
import { issbKeys, useGetActionsOfAnswer, useGetStagesOfRound, useSubmitAction } from "../../../../../hooks/query";
import { showErrorToast } from "../../../../../utils";
import { useQueryClient } from "react-query";
import LocalSpinner from "../../../../../components/LocalSpinner";
import { ArrowRight } from "@phosphor-icons/react";
import { useSelector } from "react-redux";
import { ATTESTATION_STAGES } from "../../../QuestionnaireRound/components/QuestionnaireStages";
import { useRightSidebar } from "../../../../../context/RightSidebarContext";
import { actionDisabledReason } from "utils/answerActionGate";
import assert from "utils/assert";

/**
 * Approve / Reject buttons for a question
 *
 * `beforeAction(type, proceed)` is optional. When given, every action is routed through it, and it decides
 * whether — and when — `proceed()` sends it; `proceed` returns a promise that settles once the action has
 * landed. Foundation Data v2 uses it to ask about affected answers before an approval publishes its figures
 * (ESG-4012). Absent, every action is sent straight away, as it always was.
 *
 * `hasContent` is optional too. When given, it decides whether the question has something to submit, in place of
 * "an answer or a file" — Foundation Data v2 passes whether the workstream holds any figures, since those are its
 * content and its `answer` is only notes (ESG-4021). Absent, the answer-or-files rule applies.
 */
const AnswerActionButtons = ({
  questionData,
  questionId,
  currentStageType,
  currentStageName,
  answerModifiedFlag,
  beforeAction,
  onActionDone,
  hasContent,
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [actions, setActions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // const [auditData, setAuditData] = useState(null);
  const [auditStages, setAuditStages] = useState([]);

  const { setRightSidebar, doOpen, doClose } = useRightSidebar();

  const roundSelector = useSelector((state) => state.issbRound);

  const questionnaireId = questionData?.questionnaireId || roundSelector.selectedRound?.id;

  const getRequestConfig = () => {
    return {
      onMutate: () => setIsLoading(true),
      onError: (err) => {
        setIsLoading(false);
        showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
      },
      onSettled: () => setIsLoading(false),
      onSuccess: () => {
        onActionDone?.();
        queryClient.invalidateQueries(issbKeys.actions());
        // Warning: this could trigger loss of other edits to a question (see Jira ESG-2678)
        queryClient.invalidateQueries(issbKeys.answer());
        queryClient.invalidateQueries("comments");
      },
    };
  };

  const { mutateAsync: submitAction } = useSubmitAction(getRequestConfig());

  const handleSubmitAction = (type, comment) => {
    const dataToSubmit = {
      id: questionId,
      type: type,
      comment: comment,
    };
    // mutateAsync so beforeAction can await the action landing; its own onError has already reported a
    // failure, so the rejection is only re-thrown to a caller that asked to wait on it.
    const proceed = () => submitAction(dataToSubmit);
    if (beforeAction) {
      beforeAction(type, proceed);
    } else {
      proceed().catch(() => {});
    }
  };

  const handleClickAction = (action) => {
    if (ATTESTATION_STAGES.includes(currentStageType) && action.actionType !== "WITHDRAW") {
      setRightSidebar(
        <ActionSideBar
          doClose={doClose}
          currentStageName={currentStageName}
          auditStages={auditStages}
          handleSubmitAction={handleSubmitAction}
          sidebarTitle={action.actionText}
        />
      );
    } else {
      doClose();
      handleSubmitAction(action.actionType, "");
    }
  };

  useGetActionsOfAnswer(questionId, {
    onSuccess: (data) => {
      console.log("actions! for question", questionId, data);
      setActions(data);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  assert(questionnaireId, "questionnaireId is required for get-stages-of-round");
  useGetStagesOfRound(questionnaireId, {
    enabled: !!questionnaireId, // Fetch only if we have a questionnaire
    onSuccess: (data) => {
      const currentStages = data.sort((a, b) => a.order - b.order);
      setAuditStages(currentStages);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  if (!questionId) {
    return <div>Loading...</div>;
  }

  // when to disable some actions. submit/approve must have saved edits, and an answer (or be in the EXCLUDED stage)
  const isDisabledReason = (actionType) =>
    actionDisabledReason(actionType, { question: questionData, answerModified: answerModifiedFlag, hasContent });

  return (
    <Flex direction="column">
      {isLoading && <LocalSpinner />}
      {actions?.map((action) => (
        <Button
          key={action?.actionType}
          mt={8}
          variant={action?.actionType === "SUBMIT" || action?.actionType === "APPROVE" ? "primary" : "secondary"}
          w="100%"
          onClick={() => handleClickAction(action)}
          isDisabled={!!isDisabledReason(action?.actionType)}
          title={isDisabledReason(action?.actionType)}
        >
          {action.actionText}
        </Button>
      ))}
    </Flex>
  );
}; // ./AnswerActionButtons

/**
 * Sidebar for approving / rejecting a question -- and adding a comment
 */
const ActionSideBar = ({ doClose, currentStageName, auditStages, handleSubmitAction, sidebarTitle }) => {
  const [attestationChecked, setAttestationChecked] = useState(false);
  const [comment, setComment] = useState("");

  const getNextStage = () => {
    const currentStageIndex = auditStages.findIndex((stage) => stage.name === currentStageName);
    return auditStages[currentStageIndex + 1];
  };

  const getCurrentStage = () => {
    return auditStages.find((stage) => stage.name === currentStageName);
  };

  const handleAction = (type, comment) => {
    handleSubmitAction(type, comment);
    doClose();
  };

  const attestationComment = getCurrentStage()?.comment;

  return (
    <Flex direction="column" gap={8} p={2} flexGrow={1}>
      {/* Headings */}
      <Flex direction="column" gap={2}>
        <Text color="teal" fontSize="heading.l" fontWeight="bold">
          {sidebarTitle}
        </Text>
        <Text color="gray.500" fontSize="body.m">
          The following changes will occur as a result
        </Text>
      </Flex>

      {/* Stage Change */}
      <Flex direction="column" gap={2}>
        <Text color="darkTeal" fontWeight="bold">
          Stage
        </Text>
        <Flex align="center" gap={2} mt={2}>
          <Tag bg="pink" color="black" px={3} py={1} borderRadius="md">
            {currentStageName}
          </Tag>
          <ArrowRight size={16} />
          <Tag bg="yellow.100" color="black" px={3} py={1} borderRadius="md">
            {getNextStage()?.name}
          </Tag>
        </Flex>
      </Flex>

      {/* Comment */}
      <Flex direction="column" gap={2}>
        <Text color="darkTeal" fontWeight="bold">
          Add a comment?
        </Text>
        <Textarea
          placeholder="Type here..."
          bg="pureWhite"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </Flex>

      {attestationComment && (
        <Flex>
          <Checkbox
            colorScheme="limeShade"
            color="gray.500"
            isChecked={attestationChecked}
            onChange={(e) => setAttestationChecked(e.target.checked)}
          >
            {attestationComment}
          </Checkbox>
        </Flex>
      )}

      {/* Buttons */}
      <Flex gap={2} p={2} mt={2} w="100%" justify="space-between">
        <Tooltip
          label="You need to un-check the attestation comment to reject"
          hasArrow
          isDisabled={!attestationComment || !attestationChecked}
        >
          <Button
            variant="secondary"
            flex="1"
            onClick={() => handleAction("REJECT", comment)}
            isDisabled={attestationComment && attestationChecked}
          >
            Reject
          </Button>
        </Tooltip>
        <Tooltip
          label="You need to check the attestation comment to reject"
          hasArrow
          isDisabled={!attestationComment || attestationChecked}
        >
          <Button
            variant="primary"
            flex="1"
            onClick={() => handleAction("APPROVE", comment)}
            isDisabled={attestationComment && !attestationChecked}
          >
            Approve
          </Button>
        </Tooltip>
      </Flex>
    </Flex>
  );
};

export default AnswerActionButtons;
